import type { CSSProperties } from "react";
import { AnimatedFigure } from "@/components/webUploader/AnimatedFigure";
import { EASE, kf, tr, type Stop } from "@/components/webUploader/keyframes";
import { NEW_PLANS } from "@/content/pricing/plans";

// The guides' animated diagrams (":::diagram sale-split" / ":::diagram invited-upload"), built like the
// /web-uploader animations: CSS keyframes on inline SVG (AnimatedFigure), paused off-screen and by a button,
// frozen on a meaningful frame under prefers-reduced-motion, and described in words by the caption so
// nothing is only in the animation. Colours come from the theme tokens, so both themes read.

const PAUSE = "Pause animation";
const PLAY = "Play animation";
const anim = (scope: string, el: string): CSSProperties => ({ animationName: `${scope}-${el}` });

// ---------- How one sale is split ----------
// The worked example the revenue split guide uses: 3 downloads at £10, photos Alex uploaded, in a gallery whose
// allocations give Alex 30 % of the photos Alex uploaded and a club 10 % of all sales. The money follows the
// server's rule (lib revenueSplitConfig): card processing from the fee schedule (GBP: 2.7 % + 20p) and the
// plan's commission come off first; each allocation gets its % of what's left, rounded down per person; the
// workspace keeps the rest.
// Revenue splitting is a Max plan feature, so Max's commission.
const PLAN = NEW_PLANS.find((p) => p.id === "max")!;
const pence = 3000;
const fee = Math.round(pence * 0.027) + 20;
const commission = Math.round(pence * PLAN.commissionRate);
const basis = pence - fee - commission;
const photographer = Math.floor((basis * 30) / 100);
const club = Math.floor((basis * 10) / 100);
const keeps = pence - fee - commission - photographer - club;
const gbp = (p: number) => `£${(p / 100).toFixed(2)}`;

const SALE_SPLIT = { pence, fee, commission, basis, photographer, club, keeps, commissionPercent: Math.round(PLAN.commissionRate * 1000) / 10 };

const SEGMENTS = [
  { id: "fee", label: "Card processing (fee schedule)", amount: fee, cls: "fill-muted-foreground" },
  { id: "com", label: `Photohawk commission (${SALE_SPLIT.commissionPercent}% on ${PLAN.name})`, amount: commission, cls: "fill-foreground" },
  { id: "pho", label: "Alex Moreno, 30% of photos Alex uploaded", amount: photographer, cls: "fill-hawk-orange" },
  { id: "club", label: "Riverside Running Club, 10%", amount: club, color: "#0E9384" },
  { id: "ws", label: "Your workspace keeps", amount: keeps, color: "#16A34A" },
];

const SS = "gsplit";
const SS_DUR = 13;
const SS_FREEZE = 80;
const BAR = { x: 16, y: 34, w: 368, h: 24 };
const ROW = (i: number) => 92 + i * 40; // a segment's row: its bar, then its label under it
const DROP = (i: number) => 18 + i * 12; // % of the loop when segment i drops
const SS_END = 93;

const SS_KEYFRAMES = (() => {
  const out: string[] = [];
  SEGMENTS.forEach((s, i) => {
    const t = DROP(i);
    const dy = ROW(i) - BAR.y;
    out.push(kf(`${SS}-seg${i}`, [
      // opacity on every stop: a stop without it would be interpolated towards the fade at the end
      [0, `${tr(0, 0)};opacity:1`],
      [t, `${tr(0, 0)};opacity:1;${EASE}`],
      [t + 6, `${tr(0, dy)};opacity:1`],
      [SS_END, `${tr(0, dy)};opacity:1`],
      [SS_END + 0.01, `${tr(0, 0)};opacity:0`],
      [100, `${tr(0, 0)};opacity:1`],
    ]));
    out.push(kf(`${SS}-lab${i}`, [[0, "opacity:0"], [t + 5, "opacity:0"], [t + 7, "opacity:1"], [SS_END, "opacity:1"], [SS_END + 2, "opacity:0"], [100, "opacity:0"]]));
  });
  out.push(kf(`${SS}-basis`, [[0, "opacity:0"], [DROP(2) - 2, "opacity:0"], [DROP(2), "opacity:1"], [SS_END, "opacity:1"], [SS_END + 2, "opacity:0"], [100, "opacity:0"]]));
  return out.join("");
})();

export const SaleSplitAnimation = () => {
  let x = BAR.x;
  const segs = SEGMENTS.map((s) => {
    const w = (BAR.w * s.amount) / pence;
    const seg = { ...s, x, w };
    x += w;
    return seg;
  });
  const basisX = segs[2].x;
  return (
    <AnimatedFigure
      scope={SS}
      durationS={SS_DUR}
      freezeAt={SS_FREEZE}
      keyframes={SS_KEYFRAMES}
      label="How one sale is split"
      pauseLabel={PAUSE}
      playLabel={PLAY}
      description={
        <>
          A buyer pays {gbp(pence)} for 3 downloads of photos Alex uploaded. The card processing fee from Photohawk's fee schedule ({gbp(fee)}) and the plan's
          commission ({gbp(commission)}) come off first, leaving {gbp(basis)}. Alex gets 30% of that ({gbp(photographer)}) for the photos Alex uploaded,
          the club 10% of every sale ({gbp(club)}), each rounded down, and the workspace keeps the rest ({gbp(keeps)}).
        </>
      }
    >
      <div className="overflow-hidden rounded-2xl border border-border bg-card p-3 shadow-sm sm:p-4">
        <svg viewBox="0 0 400 324" className="block h-auto w-full" role="presentation" focusable="false">
          <text x={BAR.x} y={24} className="fill-foreground" fontSize={12} fontWeight={600}>Buyer pays {gbp(pence)}</text>
          <rect x={BAR.x} y={BAR.y} width={BAR.w} height={BAR.h} rx={5} className="fill-none stroke-border" strokeDasharray="4 3" />
          <g data-a="" style={anim(SS, "basis")}>
            <path d={`M${basisX} ${BAR.y + BAR.h + 6} v6 H${BAR.x + BAR.w} v-6`} className="fill-none stroke-muted-foreground" strokeWidth={1} />
            <text x={(basisX + BAR.x + BAR.w) / 2} y={BAR.y + BAR.h + 22} textAnchor="middle" className="fill-muted-foreground" fontSize={10}>
              After fees and commission: {gbp(basis)}, the amount the %s apply to
            </text>
          </g>
          {segs.map((s, i) => (
            <g key={s.id}>
              <g data-a="" style={anim(SS, `seg${i}`)}>
                <rect x={s.x} y={BAR.y} width={Math.max(2, s.w - 1)} height={BAR.h} rx={3} className={s.cls} fill={s.color} />
              </g>
              <g data-a="" style={anim(SS, `lab${i}`)}>
                <text x={BAR.x} y={ROW(i) + BAR.h + 12} className="fill-foreground" fontSize={10.5}>{s.label}</text>
                <text x={BAR.x + BAR.w} y={ROW(i) + BAR.h + 12} textAnchor="end" className="fill-foreground" fontSize={10.5} fontWeight={600}>{gbp(s.amount)}</text>
              </g>
            </g>
          ))}
        </svg>
      </div>
    </AnimatedFigure>
  );
};

// ---------- Invited: from the email to the upload ----------
// The steps an invited photographer takes in the web uploader: the invite email, signing in, then
// Workspaces → the workspace → a gallery → a collection, and the photos going up (previews first).
const IU = "ginv";
const IU_DUR = 14;
const IU_FREEZE = 88;
const LEVELS = [
  { title: "Workspaces", items: ["Alex Kim Photography", "Riverside Events"], pick: 1 },
  { title: "Riverside Events", items: ["City Half Marathon 2026", "Riverside Sprint Triathlon", "Autumn 10K 2026"], pick: 0 },
  { title: "City Half Marathon 2026", items: ["Start", "3 mile mark", "Finish line"], pick: 2 },
];
const AT = { email: [0, 14], signin: [14, 26], level: (i: number) => [26 + i * 14, 40 + i * 14] as const, drop: [68, 95] };
const CRUMBS = ["Workspaces", "Riverside Events", "City Half Marathon 2026", "Finish line"];
const TILES = ["#ffb347", "#3fb4a4", "#9fd3e6", "#f6d365", "#f7a8b8", "#9ed27a"];

const IU_KEYFRAMES = (() => {
  const win = (el: string, from: number, to: number, fade = 1.5) =>
    kf(`${IU}-${el}`, [[0, "opacity:0"], [Math.max(0, from - fade), "opacity:0"], [from, "opacity:1"], [to, "opacity:1"], [to + fade, "opacity:0"], [100, "opacity:0"]]);
  const out = [win("email", 1.5, AT.email[1]), win("signin", AT.signin[0], AT.signin[1])];
  LEVELS.forEach((_, i) => {
    const [a, b] = AT.level(i);
    out.push(win(`lv${i}`, a, b));
    out.push(kf(`${IU}-hl${i}`, [[0, "opacity:0"], [a + 5, "opacity:0"], [a + 7, "opacity:1"], [b - 3, "opacity:1"], [b - 2, "opacity:.4"], [b - 1, "opacity:1"], [b, "opacity:0"], [100, "opacity:0"]]));
  });
  // The path grows as each level is opened: "Workspaces" with the first list, then each pick's name once it's opened.
  CRUMBS.forEach((_, j) => out.push(win(`cr${j}`, j === 0 ? AT.level(0)[0] : AT.level(j - 1)[1], AT.drop[1])));
  out.push(win("drop", AT.drop[0], AT.drop[1]));
  TILES.forEach((_, i) => {
    const t = AT.drop[0] + 3 + i * 2.2;
    const stops: Stop[] = [[0, `${tr(0, -40)};opacity:0`], [t, `${tr(0, -40)};opacity:0;${EASE}`], [t + 4, `${tr(0, 0)};opacity:1`], [AT.drop[1], `${tr(0, 0)};opacity:1`], [AT.drop[1] + 1.5, `${tr(0, 0)};opacity:0`], [100, `${tr(0, -40)};opacity:0`]];
    out.push(kf(`${IU}-t${i}`, stops));
  });
  out.push(kf(`${IU}-live`, [[0, "opacity:0"], [AT.drop[0] + 17, "opacity:0"], [AT.drop[0] + 19, "opacity:1"], [AT.drop[1], "opacity:1"], [AT.drop[1] + 1.5, "opacity:0"], [100, "opacity:0"]]));
  return out.join("");
})();

export const InvitedUploadAnimation = () => (
  <AnimatedFigure
    scope={IU}
    durationS={IU_DUR}
    freezeAt={IU_FREEZE}
    keyframes={IU_KEYFRAMES}
    label="Uploading to a workspace you've been invited to"
    pauseLabel={PAUSE}
    playLabel={PLAY}
    description={
      <>
        The invite email arrives; you sign in at upload.photohawk.com; you open Workspaces, then the workspace that invited you, a gallery
        and a collection; then you drop your photos. Their previews go up first, then the originals.
      </>
    }
  >
    <div className="overflow-hidden rounded-2xl border border-border bg-card p-3 shadow-sm sm:p-4">
      <svg viewBox="0 0 400 230" className="block h-auto w-full" role="presentation" focusable="false">
        {/* the invite email */}
        <g data-a="" style={anim(IU, "email")}>
          <rect x={60} y={40} width={280} height={150} rx={10} className="fill-secondary stroke-border" />
          <path d="M60 50 L200 120 L340 50" className="fill-none stroke-border" strokeWidth={1.5} />
          <text x={200} y={146} textAnchor="middle" className="fill-foreground" fontSize={12} fontWeight={600}>You have been invited to Riverside Events!</text>
          <text x={200} y={166} textAnchor="middle" className="fill-muted-foreground" fontSize={10}>Your temporary password is in the email</text>
        </g>
        {/* signing in */}
        <g data-a="" style={anim(IU, "signin")}>
          <rect x={110} y={34} width={180} height={160} rx={10} className="fill-background stroke-border" />
          <text x={200} y={62} textAnchor="middle" className="fill-foreground" fontSize={13} fontWeight={700}>upload.photohawk.com</text>
          <rect x={128} y={78} width={144} height={22} rx={5} className="fill-none stroke-border" />
          <text x={136} y={93} className="fill-muted-foreground" fontSize={10}>alex@example.com</text>
          <rect x={128} y={108} width={144} height={22} rx={5} className="fill-none stroke-border" />
          <text x={136} y={124} className="fill-muted-foreground" fontSize={12}>••••••••</text>
          <rect x={128} y={142} width={144} height={24} rx={6} className="fill-hawk-orange" />
          <text x={200} y={158} textAnchor="middle" fill="#1F1A17" fontSize={11} fontWeight={600}>Sign in</text>
        </g>
        {/* the path, built up level by level */}
        {CRUMBS.map((c, i) => {
          const x = 14 + [0, 82, 182, 330][i];
          return i === 0 ? null : (
            <g key={c} data-a="" style={anim(IU, `cr${i}`)}>
              <text x={x - 10} y={22} className="fill-muted-foreground" fontSize={11}>›</text>
              <text x={x} y={22} className={i === CRUMBS.length - 1 ? "fill-foreground" : "fill-muted-foreground"} fontSize={11} fontWeight={i === CRUMBS.length - 1 ? 600 : 400}>{c}</text>
            </g>
          );
        })}
        <g data-a="" style={anim(IU, "cr0")}>
          <text x={14} y={22} className="fill-muted-foreground" fontSize={11}>Workspaces</text>
          <line x1={0} y1={32} x2={400} y2={32} className="stroke-border" />
        </g>
        {LEVELS.map((lv, i) => (
          <g key={lv.title} data-a="" style={anim(IU, `lv${i}`)}>
            {lv.items.map((it, k) => (
              <g key={it} transform={`translate(14 ${46 + k * 30})`}>
                {k === lv.pick && (
                  <g data-a="" style={anim(IU, `hl${i}`)}>
                    <rect x={-6} y={-4} width={384} height={26} rx={6} className="fill-secondary" />
                  </g>
                )}
                <path d="M0 4h7l2 2.5h9v11H0z" className="fill-none stroke-hawk-orange" strokeWidth={1.4} strokeLinejoin="round" />
                <text x={28} y={15} className="fill-foreground" fontSize={11.5}>{it}</text>
                <text x={366} y={15} className="fill-muted-foreground" fontSize={11}>›</text>
              </g>
            ))}
          </g>
        ))}
        {/* the photos going up */}
        <g data-a="" style={anim(IU, "drop")}>
          <rect x={14} y={44} width={372} height={150} rx={10} className="fill-none stroke-border" strokeDasharray="5 4" />
          <text x={200} y={214} textAnchor="middle" className="fill-muted-foreground" fontSize={10.5}>Previews first, then the originals</text>
        </g>
        {TILES.map((c, i) => (
          <g key={c} data-a="" style={anim(IU, `t${i}`)}>
            <rect x={34 + i * 58} y={92} width={48} height={40} rx={5} fill={c} opacity={0.85} />
          </g>
        ))}
        <g data-a="" style={anim(IU, "live")}>
          <rect x={150} y={146} width={100} height={22} rx={11} fill="#16A34A" />
          <text x={200} y={161} textAnchor="middle" fill="#fff" fontSize={11} fontWeight={600}>Previews up</text>
        </g>
      </svg>
    </div>
  </AnimatedFigure>
);
